import React, { useEffect, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Chip } from "@mui/material";
import ActionsCell from "./actionsCell";
import { getPaceDisplay } from "../constants/pace";
import { enqueueSnackbar } from "notistack";
import { Classroom } from "../types/ClassroomModel";
import {
  getClassrooms,
  createClassroom,
  updateClassroom,
  deleteClassroom,
} from "../services/classroomService";
import EmailAvatar from "./EmailAvatar";
import { DynamicTableListTemplate } from "./shared/DynamicTableListTemplate";
import { useHandleError } from "../hooks/useHandleError";
import { useAdmin } from "../context/AdminContext";
import ClassroomForm from "./forms/ClassroomForm";

const ClassroomList: React.FC = () => {
  const handleError = useHandleError();
  const { teachers, refreshTeacherList } = useAdmin();
  const [loading, setLoading] = useState(false);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [selectedClassroom, setSelectedClassroom] = useState<
    Classroom | undefined
  >(undefined);
  const [classroomToDelete, setClassroomToDelete] =
    useState<Classroom | null>(null);
  const [hasChanged, setHasChanged] = useState(false);
  const hasFetchedRef = React.useRef(false);

  const columns: GridColDef[] = [
    {
      field: "isActive",
      headerName: "Status",
      width: 90,
      renderCell: (params) => {
        const color = params.value === true ? "success" : "default";
        const text = params.value === true ? "ACTIVE" : "DISABLED";
        return (
          <Chip
            style={{ fontSize: ".7rem" }}
            label={text}
            size="small"
            color={color}
          />
        );
      },
    },
    { field: "classroomName", headerName: "Classroom Name", width: 200 },
    {
      field: "program",
      headerName: "Program",
      width: 150,
      renderCell: (params) => {
        const colorMap: Record<string, string> = {
          "EAP YELLOW": "#f2b443",
          "EAP GREEN": "#78c0a1",
          "EAP ACCESS": "#34c1d6",
        };
        return (
          <Chip
            style={{
              fontSize: ".7rem",
              backgroundColor: colorMap[params.value] || "#ccc",
            }}
            label={params.value}
            size="small"
          />
        );
      },
    },
    {
      field: "teacher",
      headerName: "Teacher",
      width: 180,
      valueGetter: (params) => params.row?.teacher?.name || "Unassigned",
    },
    {
      field: "students",
      headerName: "Students",
      width: 90,
      valueGetter: (params) => params.row?.students?.length || 0,
    },
    {
      field: "pace",
      headerName: "Pace",
      width: 100,
      valueGetter: (params) => getPaceDisplay(params.row?.pace),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return "";
        return new Date(params.value).toLocaleDateString("en-AU");
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return "";
        const updatedBy = params.row["updatedBy"];
        const date = new Date(params.value);
        const formattedDate = date.toLocaleDateString("en-AU");
        return (
          <div
            style={{
              display: "flex",
              gap: ".25rem",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{formattedDate}</span>
            <EmailAvatar email={updatedBy} />
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Classroom;
        return (
          <ActionsCell
            onEdit={() => setSelectedClassroom(row)}
            onDelete={() => setClassroomToDelete(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchClassrooms();
      refreshTeacherList();
      hasFetchedRef.current = true;
    }
  }, []);

  const fetchClassrooms = async () => {
    setLoading(true);
    try {
      const response = await getClassrooms();
      setClassrooms(response.data);
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedClassroom(undefined);
    if (hasChanged) {
      fetchClassrooms();
      setHasChanged(false);
    }
  };

  const handleSubmit = async (classroomData: Classroom) => {
    if (classroomData._id) {
      try {
        const response = await updateClassroom(
          classroomData._id,
          classroomData
        );
        enqueueSnackbar(response.message, { variant: "success" });
        setSelectedClassroom(classroomData);
        setHasChanged(true);
      } catch (error) {
        handleError(error);
      }
    } else {
      try {
        const response = await createClassroom(classroomData);
        enqueueSnackbar(response.message, { variant: "success" });
        setSelectedClassroom(response.data);
        setHasChanged(true);
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleDeleteConfirm = async (
    row: Classroom | null | undefined
  ) => {
    if (row?._id) {
      try {
        const response = await deleteClassroom(row._id);
        enqueueSnackbar(response.message, { variant: "success" });
        fetchClassrooms();
      } catch (error) {
        handleError(error);
      }
    }
    setClassroomToDelete(null);
  };

  return (
    <DynamicTableListTemplate<Classroom>
      columns={columns}
      data={classrooms}
      loading={loading}
      selectedRow={selectedClassroom}
      FormComponent={
        <ClassroomForm
          selectedRow={selectedClassroom}
          teachers={teachers}
          onSubmit={handleSubmit}
        />
      }
      entityLabel="Classroom"
      dialogTitle="Classroom Entry Form"
      onClearSelectedRow={handleCloseDialog}
      onConfirmDelete={handleDeleteConfirm}
      entityToDelete={classroomToDelete}
    />
  );
};

export default ClassroomList;
